/**
 * WhatsApp mesaj yardımcıları.
 *
 * NEDEN wa.me: WhatsApp'ın kendisinin mesaj göndermesi (Cloud API) ayrı
 * bir numara, Meta işletme doğrulaması ve şablon onayı ister; üstelik o
 * numara normal WhatsApp uygulamasında kullanılamaz hale gelir. wa.me
 * bağlantısı ise bugün, ücretsiz ve hangi WhatsApp kullanılırsa
 * kullanılsın çalışır. Tek farkı gönder tuşuna kullanıcının basması.
 *
 * Şablon yer tutucuları burada doldurulur (sunucuda değil), çünkü dava
 * türü etiketleri ve tarih biçimleri yalnızca istemcide var.
 */

import { davaTuruEtiketi } from './constants';
import { formatDateOnly, formatDateForDisplay } from './dateUtils';

/** Büro adı — şablonlardaki {buro} yer tutucusu için. */
export const BURO_ADI = 'Öztürk Hukuk ve Danışmanlık';

/**
 * Müvekkile gönderilen bağlantılar.
 *
 * Şablonların içine adres yazmak yerine {uygulama} ve {giris} yer
 * tutucuları kullanılıyor; adres değişirse burası güncellenir, yedi
 * şablonun metni değil.
 *
 * GİRİŞ bağlantısı "?giris=muvekkil" taşır: müvekkil telefondan
 * dokunduğunda giriş türü kartını seçmeden doğrudan müvekkil formuna
 * düşer. Birkaç saniyelik fark gibi görünür ama uygulamayı ilk kez
 * açan bir müvekkil için "hangi kutuya basacağım" sorusunu tamamen
 * ortadan kaldırıyor.
 */
export const UYGULAMA_ADRESI = 'https://izzetbaranozturk.com';
export const GIRIS_ADRESI = UYGULAMA_ADRESI + '/login?giris=muvekkil';

/**
 * App Store adresi — KANONİK biçimde yazılmak zorunda.
 *
 * Kısa "apps.apple.com/app/id6797491601" biçimi 301 yönlendirme döner
 * ve yönlendirme yanıtının gövdesi boştur. WhatsApp önizleme için
 * yönlendirmeyi takip etmediğinden og:title bulamaz ve karta yalnızca
 * "apps.apple.com" yazar. Türkçe harfleri sadeleştirilmiş ASCII hali de
 * yönlendirmeye düşüyor. Uygulama adının ve ikonunun WhatsApp kartında
 * çıktığı tek biçim, Apple'ın yüzde kodlu kanonik adresi:
 */
export const APP_STORE_ADRESI =
  'https://apps.apple.com/tr/app/'
  + '%C3%B6zt%C3%BCrk-hukuk-ve-dan%C4%B1%C5%9Fmanl%C4%B1k/id6797491601';

/**
 * Telefonu WhatsApp'ın beklediği biçime getirir: ülke kodu dahil,
 * yalnızca rakam. "0532 123 45 67" -> "905321234567".
 * Tanınmayan / eksik numarada null döner ki arayüz "telefon yok" desin.
 */
export const waNumara = (ham) => {
  if (!ham) return null;
  let r = String(ham).replace(/\D/g, '');
  if (!r) return null;
  if (r.length === 11 && r.startsWith('0')) r = r.slice(1);
  if (r.length === 10 && r.startsWith('5')) r = '90' + r;
  if (r.length < 10 || r.length > 15) return null;
  return r;
};

/**
 * Dosyadan yer tutucu değerlerini çıkarır.
 * Bilgisi olmayan alan boş string döner — mesajda "undefined" görünmesin.
 */
export const dosyaDegerleri = (dosya) => {
  if (!dosya) return {};
  const al = (...adlar) => {
    for (const a of adlar) {
      const v = dosya[a];
      if (v !== null && v !== undefined && String(v).trim() !== '') return v;
    }
    return '';
  };

  const durusma = al('DurusmaTarihi', 'durusmaTarihi');

  const esas = String(al('MahkemeEsas', 'mahkemeEsas'));
  const icra = String(al('IcraDosyaNo', 'icraDosyaNo'));
  const tahkim = String(al('TahkimBasvuruNo', 'tahkimBasvuruNo'));

  /* Müvekkile gösterilecek dosya numarası.
     Föy no büronun iç numarasıdır, müvekkil onu tanımaz; onun bildiği
     numara mahkeme esası, icra dosya numarası ya da tahkim başvuru
     numarasıdır. Hiçbiri yoksa numara yerine dava türü yazılır. */
  const dosyaNo = esas || icra || tahkim || davaTuruEtiketi(dosya) || '';

  return {
    muvekkil: String(al('Muvekkil', 'muvekkil')),
    foyNo: String(al('FoyNo', 'foyNo')),
    dosyaNo,
    davaTuru: davaTuruEtiketi(dosya),
    mahkeme: String(al('MahkemaBilgisi', 'mahkemaBilgisi', 'MahkemeBilgisi', 'mahkemeBilgisi')),
    esas: String(al('MahkemeEsas', 'mahkemeEsas')),
    icraDosyaNo: String(al('IcraDosyaNo', 'icraDosyaNo')),
    tahkimBasvuruNo: String(al('TahkimBasvuruNo', 'tahkimBasvuruNo')),
    /* Saatiyle birlikte: "03.10.2026 10:00". Müvekkilin duruşmaya
       yetişebilmesi için saat şart. */
    durusmaTarihi: durusma ? formatDateForDisplay(durusma) : '',
    /* Yalnızca gün gerekiyorsa. */
    durusmaGunu: durusma ? formatDateOnly(durusma) : '',
    buro: BURO_ADI,
    uygulama: UYGULAMA_ADRESI,
    giris: GIRIS_ADRESI,
    appStore: APP_STORE_ADRESI,
    tc: String(al('MuvekkilTC', 'muvekkilTC', 'MuvekkilTc', 'muvekkilTc')),
  };
};

/**
 * Şablondaki {anahtar} yer tutucularını değerlerle değiştirir.
 *
 * GÜVENLİK AĞI: Müvekkile giden mesajda FÖY NUMARASI YER ALMAZ. Eski
 * bir şablonda {foyNo} kalmışsa bile föy numarası değil, müvekkilin
 * tanıdığı dosya numarası (esas / icra / tahkim) yazılır.
 */
export const sablonDoldur = (metin, degerler) =>
  String(metin || '').replace(/\{(\w+)\}/g, (tam, anahtar) => {
    if (anahtar === 'foyNo') {
      return String(degerler.dosyaNo ?? '').trim() || tam;
    }
    const v = degerler[anahtar];
    return v === undefined || v === null ? tam : String(v);
  });

/**
 * Açılabilir wa.me bağlantısı üretir.
 * Numara yoksa null döner — çağıran taraf düğmeyi pasif göstermeli.
 */
export const waBaglanti = (numara, metin) => {
  const n = waNumara(numara);
  if (!n) return null;
  const m = String(metin || '').trim();
  return 'https://wa.me/' + n + (m ? '?text=' + encodeURIComponent(m) : '');
};

/** Şablonda kullanılabilecek yer tutucular — ayarlar ekranında gösterilir. */
export const YER_TUTUCULAR = [
  ['{muvekkil}', 'Müvekkilin adı'],
  ['{dosyaNo}', 'Dosya numarası (esas / icra / tahkim)'],
  ['{durusmaTarihi}', 'Duruşma tarihi ve saati'],
  ['{durusmaGunu}', 'Duruşma günü (saatsiz)'],
  ['{davaTuru}', 'Dava türü'],
  ['{mahkeme}', 'Mahkeme bilgisi'],
  ['{esas}', 'Esas numarası'],
  ['{icraDosyaNo}', 'İcra dosya numarası'],
  ['{tahkimBasvuruNo}', 'Tahkim başvuru numarası'],
  ['{buro}', 'Büro adı'],
  ['{uygulama}', 'Uygulama adresi'],
  ['{giris}', 'Müvekkil giriş bağlantısı'],
  ['{appStore}', 'App Store bağlantısı'],
  ['{tc}', 'Müvekkilin TC kimlik no'],
  ['{girisKodu}', 'Tek kullanımlık giriş kodu (gönderirken üretilir)'],
];

/** Şablon, gönderirken üretilecek giriş kodunu istiyor mu? */
export const kodIstiyor = (metin) => String(metin || '').includes('{girisKodu}');

/**
 * Şablonda geçen ama dosyada KARŞILIĞI BOŞ olan alanların adlarını döner.
 *
 * Neden gerekli: doldurucu, değeri boş olan bir alanı sessizce siliyor.
 * Dosyada duruşma tarihi yazmıyorsa mesaj "duruşması  tarihindedir"
 * diye yarım bir cümleyle gidiyor ve müvekkile sistemin hatası gibi
 * görünüyor. Pencere bu listeyi gösterip göndermeden önce uyarıyor.
 */
export const bosAlanlar = (metin, degerler) => {
  const etiket = Object.fromEntries(
    YER_TUTUCULAR.map(([y, a]) => [y.slice(1, -1), a]),
  );
  const eksik = new Set();
  String(metin || '').replace(/\{(\w+)\}/g, (tam, anahtar) => {
    const k = anahtar === 'foyNo' ? 'dosyaNo' : anahtar;
    const v = degerler[k];
    // undefined/null = yer tutucu metinde aynen kalır, gözden kaçmaz.
    // Asıl tehlike BOŞ METİN: iz bırakmadan siliniyor.
    if (v !== undefined && v !== null && String(v).trim() === '') {
      eksik.add(etiket[k] || k);
    }
    return tam;
  });
  return [...eksik];
};

/**
 * Uygulama icinde WhatsApp'i DOGRUDAN acar.
 *
 * SORUN: uygulama bir WKWebView; icindeki wa.me bagi once Safari'yi
 * aciyor, Safari de WhatsApp'a yonlendiriyordu. Iki sicrama hem yavas
 * hem de bazen bos sayfada takiliyordu.
 *
 * COZUM: uygulamadaysak once "whatsapp://send" semasini deniyoruz —
 * bu dogrudan WhatsApp'i acar. Cihazda WhatsApp kurulu degilse
 * eski wa.me yoluna dusuyoruz.
 *
 * Info.plist'te LSApplicationQueriesSchemes icinde "whatsapp" kayitli
 * olmali, yoksa iOS semayi acamadigini soyler.
 */
export const whatsappAc = async (numara, mesaj) => {
  const temiz = String(numara || '').replace(/[^0-9]/g, '');
  const metin = encodeURIComponent(mesaj || '');
  const waMe = 'https://wa.me/' + temiz + (metin ? '?text=' + metin : '');

  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor?.isNativePlatform?.()) {
      const { AppLauncher } = await import('@capacitor/app-launcher');
      const sema = 'whatsapp://send?phone=' + temiz + (metin ? '&text=' + metin : '');
      const { value } = await AppLauncher.canOpenUrl({ url: 'whatsapp://send' });
      if (value) {
        await AppLauncher.openUrl({ url: sema });
        return true;
      }
      await AppLauncher.openUrl({ url: waMe });
      return true;
    }
  } catch (e) {
    /* Eklenti yoksa asagidaki klasik yol calisir. */
  }

  window.open(waMe, '_blank', 'noopener,noreferrer');
  return true;
};
